import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import {t} from "i18next";
import {debounce} from "lodash";
import moment from "moment";
import PropTypes from "prop-types";

import ArrowDown from "../../images/icons/arrowDown.png";
import ArrowUp from "../../images/icons/arrowUp.png";
import ContextMenuCursor from "../../images/icons/contextMenu.png";
import ContextMenuDoubleClickCursor from "../../images/icons/contextMenuAndDoubleClick.png";
import DoubleClickCursor from "../../images/icons/doubleClick2.png";
import {COLOR_SCHEME} from "../../utilities/colorSheme";
import {ICON_NAMES} from "../../utilities/generalConstants.js/generalConstants";
import {toaster} from "../../utilities/hooks/toaster";
import {KEYBOARD_CODES} from "../../utilities/keyboardCodes";
import {UI_PERMISSION_TYPE} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";
import Icon from "../Icon/Icon";
import Spinner from "../spinner/Spinner";

import CopyCell from "./reactTable/CopyCell";
import IndeterminateCheckbox from "./reactTable/IndeterminateCheckbox";
import ClickableTr from "./ClickableTr";

//* add pre to defaultData object for row preformat

//* add to data item: isAccented(accent row), isBlured(low opacity row)
//* add to data item: checkboxDisabled to disable row checkbox
//* за замовчуванням фільтрування колонки вимкнено
//* щоб активувати потрібно додати `enableColumnFilter: true`
//* та функцію для сортування, якщо це текст: filterFn: "includesString".
//* За потреби можна створити власну функцію.

//* Lower overall row:
// paymentTableData?.push({
//   id: "overall",
//   date: t("Overall"),
//   requestSum: numberFormat(overallRequestSum) || "",
//   advanceSum: numberFormat(overallAdvanceSum) || "",
//   remainderSum: numberFormat(overallRemainderSum) || "",
//   inOrder: true,
// });

const ReactTable = ({
  defaultData = [],
  columns,
  columnVisibility = {},
  onSelect = () => {},
  current,
  className = "",
  onRowDoubleClick,
  tableClassName = "",
  loading = false,
  selectable = true, /// чи можна обирати один рядок
  setMultiSelect = () => {}, // засетити масив id обраних чекбоксів
  defaultMultiSelection, // масив id обраних чекбоксів
  enableMultiSelection = false, /// мультиселект (чекбокси)
  disabledMultiSelection = false,
  disabledAllRowsCheckbox = false, //заблокировать общий чекбокс в шапке таблицы
  disabledRowIds = [], // массив ID строк, которые нужно задизейблить
  lineThroughRowIds = [],
  accentedRowIds = [], // массив ID строк, которые нужно выделить цветом
  accentColor = COLOR_SCHEME.ACCENT_TABLE_ROW, // цвет выделения (для бекграунда)
  accentTextColor, //или для самого текста
  disabledRowClassName = "react-table-disabled-td",
  enableColumnResizing = false,
  headerCheckboxRight = null,

  paginatable = false,
  onPaginate = () => {},
  paginationPageView = false, //currentPage-totalPages view
  currentPage = 1,
  totalPages = 1,

  scrollTop = false,
  scrollToCurrent = false,
  handleContextMenu = null,
  selectOnRightBtn = false,

  footerColSpan, //кількість пропущених колонок до назви колонки футера
  footerValues, //масив значень колонок футера

  showEmptyStateIcon = false, //показувати іконку пустого стану, якщо даних немає,
  readOnly = false, //заблокировать все взаимодействия с таблицей, включая выбор строк и отображение курсора указателя
  uiPermissionType = UI_PERMISSION_TYPE.WRITABLE,
  meta, //whole table data, can be used in columns via table.options.meta
  ...props
}) => {
  const [tablefullWidth, setTableFullWidth] = useState(null);

  const data = defaultData ?? [];

  const SORTING_STATES = Object.freeze({ASC: "asc", DESC: "desc"});
  const [sorting, setSorting] = useState([]);

  const [rowSelection, setRowSelection] = useState(null);
  const [columnFilters, setColumnFilters] = useState([]);

  //*** Table Row Selection on Tab ***
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [copyCell, setCopyCell] = useState(null);

  const debouncedPagination = debounce(onPaginate, 1000);

  const tableContainerRef = useRef(null);
  const tableRef = useRef(null);
  const rowRefs = useRef(new Map());

  useEffect(() => {
    const updateWidth = () => {
      if (tableContainerRef.current) {
        setTableFullWidth(tableContainerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    if (scrollTop) tableContainerRef.current.scrollTop = 0;
  }, [scrollTop]);

  useEffect(() => {
    if (scrollToCurrent && current) {
      rowRefs.current.get(current)?.scrollIntoView();
    }
  }, [scrollToCurrent, current]);

  const handleScroll = () => {
    const {scrollTop, clientHeight, scrollHeight} = tableContainerRef.current;
    if (scrollTop + clientHeight > scrollHeight - 5) {
      debouncedPagination();
    }
  };

  const onTabKeyPress = () => {
    if (readOnly) return;
    let index = selectedIndex !== null && selectedIndex < data.length - 1 ? selectedIndex + 1 : 0;
    let isNotDisabled = false;
    while (!isNotDisabled) {
      if (disabledRowIds?.includes(data[index]?.id)) index++;
      else isNotDisabled = true;
    }
    setSelectedIndex(index);
    onSelect(data[index]?.id);
  };

  function onTableKeyDown(e) {
    if (readOnly) return;

    const isModifierKey = e.ctrlKey || e.altKey || e.metaKey || e.shiftKey;

    // Не блокируем события для сочетаний клавиш
    if (isModifierKey) {
      return;
    }

    switch (e.code) {
      case KEYBOARD_CODES.TAB:
        e.preventDefault();
        onTabKeyPress();
        break;
    }
  }
  //*** //Table Row Selection on Tab ***

  const getRowSelectionFromDefault = () => {
    return data?.reduce(
      (acc, row) => (defaultMultiSelection?.includes(row.id) ? {...acc, [row.number - 1]: true} : acc),
      {},
    );
  };
  const getDefaultFromRowSelection = newRowSelection => {
    return (
      Object.keys(newRowSelection || {}).map(id => {
        return data[+id]?.id;
      }) ?? {}
    );
  };

  const onDefaultRowSelectionChange = updater => {
    if (readOnly) return;

    const newSelection = typeof updater === "function" ? updater(getRowSelectionFromDefault()) : updater;

    setMultiSelect(getDefaultFromRowSelection(newSelection));
  };

  const table = useReactTable({
    data,
    columns,
    meta,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: enableMultiSelection,
    state: {
      rowSelection: defaultMultiSelection ? getRowSelectionFromDefault() : rowSelection || {},
      sorting,
      columnVisibility,
      columnFilters,
    },
    defaultColumn: {
      minSize: 40,
      maxSize: 800,
      enableColumnFilter: false,
    },
    onRowSelectionChange: defaultMultiSelection ? onDefaultRowSelectionChange : setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    sortingFns: {
      sortDate: (rowA, rowB, columnId) => {
        const parseDate = dateStr => {
          if (dateStr && typeof dateStr === "object") {
            const [, date] = dateStr.text.split(/\n/) || [];
            const m = moment(date || dateStr.text, "DD.MM.YYYY HH:mm:ss");
            return m.isValid() ? m : moment(0);
          }
          const m = moment(dateStr, "DD.MM.YYYY HH:mm:ss");
          return m.isValid() ? m : moment(0);
        };

        const dateA = parseDate(rowA.original[columnId]);
        const dateB = parseDate(rowB.original[columnId]);

        return dateB.diff(dateA);
      },
      sortNumber: (rowA, rowB, columnId) => {
        const numA = parseFloat(rowA.original[columnId]);
        const numB = parseFloat(rowB.original[columnId]);

        return numA - numB;
      },
      sortSum: (rowA, rowB, columnId) => {
        const clean = value =>
          parseFloat(
            String(value)
              .replace(/\s/g, "")
              .replace(",", ".")
              .replace(/[^\d.-]/g, ""),
          );
        const numA = clean(rowA.original[columnId]) || 0;
        const numB = clean(rowB.original[columnId]) || 0;

        return numA - numB;
      },
      sortString: (rowA, rowB, columnId) =>
        (rowA.original[columnId] || "").localeCompare(rowB.original[columnId] || ""),
    },
    enableColumnResizing: enableColumnResizing,
    columnResizeMode: "onChange",
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    if (enableMultiSelection && !defaultMultiSelection && Object.keys(rowSelection ?? {}).length) setRowSelection({});
  }, [data.length, enableMultiSelection, !defaultMultiSelection]);

  useEffect(() => {
    if (
      !enableMultiSelection
      // || defaultMultiSelection
    )
      return;

    const selectedIds = Object.keys(rowSelection || {}).map(id => {
      return defaultData[+id]?.id;
    });

    setMultiSelect([...(Array.isArray(defaultMultiSelection) ? defaultMultiSelection : []), ...selectedIds]);
  }, [
    enableMultiSelection,
    !!defaultMultiSelection,
    Array.isArray(defaultMultiSelection) && defaultMultiSelection?.join(""),
    JSON.stringify(rowSelection ?? {}),
    data?.length,
  ]);

  const handleRowClick = row => {
    if (readOnly) return;

    if (Array.isArray(disabledRowIds) && disabledRowIds?.includes(row?.original?.id)) {
      return;
    }
    if (current === row.original.id && !copyCell) {
      onSelect(null);
      setSelectedIndex(null);
    } else {
      onSelect(row.original.id);
      setSelectedIndex(row.index);
    }
  };

  const handleRowDoubleClick = row => {
    onRowDoubleClick && onRowDoubleClick(row.original);
  };

  function measureTextWidth(text) {
    var tempSpan = document.createElement("span");
    tempSpan.style.visibility = "hidden";
    tempSpan.textContent = text;

    document.body.appendChild(tempSpan);
    var width = tempSpan.getBoundingClientRect().width;
    document.body.removeChild(tempSpan);

    return width + 10;
  }

  const resizableHeaderStyles = {
    padding: "0 0.5em",
    position: "relative",
  };
  const getResizeHeaderWidth = id => `calc(var(--header-${id}-size) * 1px)`;
  const getHeaderWidth = header => (header?.getSize() === 150 ? "max-content" : header?.getSize());
  const getCellWidth = size => (size === 150 ? "max-content" : size);

  const tHeadRender = useCallback(() => {
    return table.getHeaderGroups().map(headerGroup => (
      <tr key={headerGroup.id}>
        {enableMultiSelection && (
          <th
            key={"i0"}
            style={{
              width: 25,
            }}
            className="stickyTableRowItem"
          >
            <div className="row aic jcc gap5">
              <IndeterminateCheckbox
                {...{
                  checked: table.getIsAllRowsSelected(),
                  disabled: disabledMultiSelection || disabledAllRowsCheckbox || readOnly,
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler(),
                }}
                uiPermissionType={uiPermissionType}
              />
              {headerCheckboxRight}
            </div>
          </th>
        )}
        {headerGroup.headers.map(header => (
          <th
            className={`${header.column.columnDef.isSortable ? "sortableHeader" : ""}`}
            key={header.id}
            style={{
              padding: "0 0.5em",
              ...(header.column.getCanResize()
                ? resizableHeaderStyles
                : {maxWidth: header.column.columnDef?.maxSize ? header.column.columnDef?.maxSize : "max-content"}),
              width: header.column.getCanResize() ? getResizeHeaderWidth(header?.id) : getHeaderWidth(header),
            }}
            title={(() => {
              const t = flexRender(header.column.columnDef.header, header.getContext());
              return typeof t === "string" || typeof t === "number" ? String(t) : undefined;
            })()}
            onClick={header.column.columnDef.isSortable ? header.column.getToggleSortingHandler() : () => {}}
          >
            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
            {header.column.columnDef.enableColumnFilter && (
              <input
                value={header.column.getFilterValue() ?? ""}
                onChange={e => header.column.setFilterValue(e.target.value)}
                placeholder={t("Search...")}
                style={{marginLeft: "1px", marginRight: "1px"}}
              />
            )}
            {header.column.getIsSorted() === SORTING_STATES.ASC && (
              <img src={ArrowUp} width={10} height={10} style={{marginLeft: "5px"}} />
            )}
            {header.column.getIsSorted() === SORTING_STATES.DESC && (
              <img src={ArrowDown} width={10} height={10} style={{marginLeft: "5px"}} />
            )}
            {header.column.getCanResize() && (
              <div
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
              />
            )}
          </th>
        ))}
      </tr>
    ));
  }, [disabledAllRowsCheckbox, readOnly]);

  const tCGRender = useCallback(() => {
    const cols = table.getHeaderGroups()[0].headers.map(header => (
      <col
        key={header.id}
        style={{
          width: header.getSize() === 150 ? measureTextWidth(header.getContext()) : header.getSize(),
          maxWidth: "max-content",
        }}
      />
    ));
    if (enableMultiSelection) {
      cols.unshift(<col key={"1e"} style={{width: 25}} />);
    }
    return cols;
  }, []);

  // мультилайновые ячейки [...]
  const expandedCellRefs = [];

  const setref = ref => {
    expandedCellRefs.push(ref);
  };

  const onExpandingCellClick = id => {
    if (id !== null && expandedCellRefs[id]?.classList.contains("expanded")) {
      expandedCellRefs[id].classList.remove("expanded");
      return;
    }
    expandedCellRefs.forEach(item => item.classList.remove("expanded"));
    if (id !== null) {
      expandedCellRefs[id]?.classList.add("expanded");
    }
  };
  //

  const handleCellClick = (e, text, rowId) => {
    if (e.target.tagName !== "SPAN" || typeof text !== "string") {
      return;
    }
    if (current !== rowId && selectable) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const tableRect = tableRef.current.getBoundingClientRect();

    setCopyCell({
      text,
      top: rect.top - tableRect.top,
      left: rect.left - tableRect.left,
      width: rect.width,
      height: rect.height,
    });
  };

  const handleClose = () => {
    setCopyCell(null);
  };

  const handleCopy = () => {
    if (copyCell?.text) {
      navigator.clipboard.writeText(copyCell.text).then(() => {
        toaster.success("Text copied to clipboard!");
      });
    }
  };

  const columnSizeVars = useCallback(() => {
    const headers = table.getFlatHeaders();
    let colSizes = {};
    const factor =
      tablefullWidth && tablefullWidth > table.getTotalSize()
        ? (tablefullWidth - table.getTotalSize()) / columns?.length
        : 0; // если ширина контейнера таблицы больше чем получаемая сумма ширин столбцов таблицы то добавляем к каждой по коефициенту дабы добиться ста процентной ширины таблицы

    for (let i = 0; i < headers.length; i++) {
      const header = headers?.[i];
      colSizes[`--header-${header.id}-size`] = header?.getSize() + factor;
      colSizes[`--col-${header.column.id}-size`] = header?.column?.getSize() + factor;
    }
    return colSizes;
  }, [
    JSON.stringify(table.getState().columnSizingInfo),
    JSON.stringify(table.getState().columnSizing),
    tablefullWidth,
  ]);

  const tbodyRender = () => {
    var expandedCelsCounter = 0;
    return table.getRowModel().rows.map(row => {
      return (
        <ClickableTr
          key={row.original.id}
          onClick={() => {
            if (readOnly) return;

            if (selectable) {
              handleRowClick(row);
            }
          }}
          onDoubleClick={() => {
            if (onRowDoubleClick) handleRowDoubleClick(row);
          }}
          onContextMenu={e => {
            if (selectable && selectOnRightBtn && current !== row.original.id) {
              handleRowClick(row);
            }

            if (handleContextMenu) {
              handleContextMenu(e, row.original.id);
            }
          }}
          style={{
            ...(accentedRowIds?.includes(row?.original?.id) || row.original.isAccented
              ? {...(accentTextColor ? {color: accentTextColor} : {backgroundColor: accentColor})}
              : {}),
            ...(row.original.isBlured ? {opacity: 0.7} : {}),
            borderBottom: `1px solid ${row.getIsSelected() ? COLOR_SCHEME.BLUE2 : "transparent"}`,
          }}
          className={clsx(
            !readOnly && "reactTableRow",
            Array.isArray(disabledRowIds) && disabledRowIds?.includes(row?.original?.id) && disabledRowClassName,
            lineThroughRowIds?.includes(row?.original.id) && "react-table-linethrough",
            // `${disabledRowIds?.includes(row?.original?.id) ? disabledRowClassName : ""} ${
            //   lineThroughRowIds?.includes(row?.original.id) ? "react-table-linethrough " : ""
            // }`,
          )}
          data-readonly={readOnly}
        >
          {enableMultiSelection && (
            <td
              key={"j0"}
              style={{
                width: 20,
                backgroundColor:
                  selectable && row?.original?.id === current
                    ? accentedRowIds?.includes(row.original.id)
                      ? COLOR_SCHEME.ALERT
                      : "#567db8"
                    : "transparent",
                textAlign: "center",
                cursor: "pointer",
              }}
              className="stickyTableRowItem"
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled:
                    disabledMultiSelection ||
                    row?.original?.checkboxDisabled ||
                    (Array.isArray(disabledRowIds) && disabledRowIds?.includes(row?.original?.id)) ||
                    readOnly,
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                  title: row?.original?.checkboxTitle ?? "",
                }}
                style={{cursor: "pointer"}}
                uiPermissionType={uiPermissionType}
              />
            </td>
          )}
          {row.getVisibleCells().map(cell => {
            const value = cell.getValue();
            const isRowWithPreFormat = row.original.pre;
            const cellvalue = typeof value === "object" ? value?.text : value;
            var expandedCelsCounter_ = expandedCelsCounter;
            if (value?.expand) {
              expandedCelsCounter++;
            }
            return (
              <td
                onClick={e => handleCellClick(e, cell.getValue(), row.original.id)}
                key={cell.id}
                title={
                  typeof cellvalue === "string" || typeof cellvalue === "number" ? t(String(cellvalue)) : undefined
                }
                ref={element => {
                  if (value?.expand) setref(element);
                  rowRefs.current.set(row.original.id, element);
                }}
                style={{
                  ...(cell.column.getCanResize()
                    ? {
                        width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                        // `var(--col-${cell.column.id}-size) * 1px)`,
                      }
                    : {maxWidth: getCellWidth(cell.column.columnDef?.size)}),
                  backgroundColor:
                    selectable && row?.original?.id === current
                      ? accentedRowIds?.includes(row.original.id)
                        ? COLOR_SCHEME.ALERT
                        : "#567db8"
                      : "inherit",
                  color: selectable && row?.original?.id === current ? "white" : "inherit",
                  cursor:
                    !readOnly &&
                    selectable &&
                    Array.isArray(disabledRowIds) &&
                    disabledRowIds?.includes(row?.original?.id)
                      ? "not-allowed"
                      : handleContextMenu && onRowDoubleClick && selectOnRightBtn
                      ? `url(${ContextMenuDoubleClickCursor}), pointer`
                      : handleContextMenu && selectOnRightBtn
                      ? `url(${ContextMenuCursor}), pointer`
                      : onRowDoubleClick
                      ? `url(${DoubleClickCursor}) 16 16, auto`
                      : !readOnly && selectable
                      ? "pointer"
                      : "default",
                  position: "relative",
                  ...(cell?.column?.columnDef?.alignStart ? {textAlign: "start"} : {}),
                  ...(cell?.column?.columnDef?.textAlign ? {textAlign: cell?.column?.columnDef?.textAlign} : {}),
                }}
              >
                {value?.expand && (
                  <span
                    className="expand_sign"
                    onClick={e => {
                      e.stopPropagation();
                      onExpandingCellClick(expandedCelsCounter_);
                    }}
                  >
                    {`[...]`}
                  </span>
                )}

                {value?.expand ? (
                  <span className="expand_cell_text">
                    <pre style={{whiteSpace: "pre-wrap", overFlowWrap: "break-word", overFlow: "hidden"}}>
                      {cellvalue}
                    </pre>
                  </span>
                ) : isRowWithPreFormat ? (
                  <pre style={{whiteSpace: "pre-wrap", overFlowWrap: "break-word", overFlow: "hidden"}}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </pre>
                ) : (
                  <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                )}
              </td>
            );
          })}
        </ClickableTr>
      );
    });
  };

  const memoizedBody = useMemo(
    () => tbodyRender(),
    [
      JSON.stringify(defaultData),
      JSON.stringify(current),
      disabledRowIds,
      defaultMultiSelection,
      JSON.stringify(table.getState().rowSelection ?? {}),
      JSON.stringify(table.getState().columnFilters ?? []),
      JSON.stringify(table.getState().sorting ?? []),
    ],
  );

  useEffect(() => {
    if (!paginatable) return;
    if (defaultData?.length === 0) return;
    const {clientHeight: containerHeight} = tableContainerRef.current;
    const {clientHeight: tableHeight} = tableRef.current;
    if (containerHeight > tableHeight) {
      debouncedPagination();
    }
  }, [defaultData?.length]);

  const emptyStateIconSize =
    Math.max(30, Math.min(80, Math.round(tableContainerRef.current?.clientHeight * 0.35))) || 60;

  return (
    <div
      className={`project_list relative fcCol jcsb aist ${className}`}
      style={props.style}
      onKeyDown={onTableKeyDown}
      tabIndex="0"
      ref={tableContainerRef}
      onScroll={paginatable ? handleScroll : () => {}}
    >
      <div className="w100">
        <table
          ref={tableRef}
          style={{
            ...(enableColumnResizing ? {...columnSizeVars(), width: table.getTotalSize()} : {}),
          }}
          className={`project_table autoLayout ${tableClassName} ${enableColumnResizing && "fixedLayout"}`}
        >
          {!enableColumnResizing && <colgroup>{tCGRender()}</colgroup>}
          <thead>{tHeadRender()}</thead>
          <tbody>{memoizedBody}</tbody>
          {footerValues && Array.isArray(footerValues) && (
            <tfoot className="project_table">
              <tr
                style={{
                  padding: "0 0.5em",
                  color: "#426ba6",
                  fontWeight: 500,
                  fontStyle: "normal",
                  borderBottom: "0.01px solid rgba(88, 89, 93, 0.2941176471)",
                }}
              >
                {footerValues.map((value, index) => (
                  <td
                    key={value}
                    colSpan={index === 0 && (footerColSpan ?? columns?.length - footerValues?.length + 2)}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {loading && <Spinner />}
      {copyCell && <CopyCell copyCell={copyCell} handleClose={handleClose} handleCopy={handleCopy} />}
      {showEmptyStateIcon && !loading && defaultData && (data?.length ?? 0) === 0 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "#5565a3",
            pointerEvents: "none",
            padding: "0 1rem",
            opacity: 0.7,
          }}
        >
          {/* <img
            src={EmptyStateIcon}
            alt="No data"
            style={{width: emptyStateIconSize, height: emptyStateIconSize, opacity: 0.6}}
          /> */}
          <Icon
            name={ICON_NAMES.DOC_ERROR}
            className="icon-react-table"
            width={emptyStateIconSize}
            height={emptyStateIconSize}
          />
          <span>{t("No Data")}</span>
        </div>
      )}
      {paginatable && paginationPageView && (
        <div
          className="row sticky pr jcfe italic asfs"
          style={{
            bottom: "0px",
            left: 0,
            backgroundColor: "white",
            opacity: "0.8",
            zIndex: "100",
            width: "70px",
            height: "15px",
          }}
        >
          <span>{`${+currentPage <= +totalPages ? +currentPage ?? 1 : +totalPages ?? 1} / ${+totalPages ?? 1}`}</span>
        </div>
      )}
    </div>
  );
};

ReactTable.propTypes = {
  defaultData: PropTypes.array,
  columns: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  current: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])]),
  className: PropTypes.string,
  // onRowDoubleClick: PropTypes.func,
  props: PropTypes.array,
  headerCheckboxRight: PropTypes.node,
  showEmptyStateIcon: PropTypes.bool,
};

export default ReactTable;
